/**
 * Utility functions for iframe height resizing and scroll handling (vanilla JS)
 */

const SCROLL_EXCLUDED_ELEMENTS = ["canvas"] as const; // can add more elements to the list if needed

export interface IframeResizeOptions {
  // Optional callback when height changes
  onHeightChange?: (height: number) => void;
  // Whether to enable wheel event handling for parent scrolling
  enableScrollEvents?: boolean;
  // Optional custom message processor for handling non-standard message formats
  customMessageProcessor?: (
    event: MessageEvent
  ) => { type: IframeMessageType; height?: number; deltaY?: number } | null;
}

// Add message type constants
export const IFRAME_MESSAGE_TYPES = {
  RESIZE: "resize",
  SCROLL: "scroll",
} as const;

export type IframeMessageType =
  (typeof IFRAME_MESSAGE_TYPES)[keyof typeof IFRAME_MESSAGE_TYPES];

/**
 * Checks if the event is an iframe message type
 */
export function isIframeMessage(event: MessageEvent): boolean {
  if (!event.data || typeof event.data !== "object") return false;

  return (
    event.data.type === IFRAME_MESSAGE_TYPES.RESIZE ||
    event.data.type === IFRAME_MESSAGE_TYPES.SCROLL
  );
}

/**
 * Creates a message handler for iframe height resizing and scroll events
 * @param iframe The iframe element to resize
 * @param options Configuration options
 * @returns A function to remove the event listener
 */
export function setupIframeHeightListener(
  iframe: HTMLIFrameElement,
  options: IframeResizeOptions = {}
): () => void {
  const {
    onHeightChange,
    enableScrollEvents = false,
    customMessageProcessor,
  } = options;

  const handleResizeMessage = (height: number | string) => {
    const numericHeight = Number(height);
    const iframeElement = iframe;

    // Update iframe height
    iframeElement.style.height = `${numericHeight}px`;
    iframeElement.style.minHeight = `${numericHeight}px`;

    if (onHeightChange) {
      onHeightChange(numericHeight);
    }
  };

  const handleScrollMessage = (deltaY: number) => {
    if (enableScrollEvents) {
      window.scrollBy({
        top: deltaY,
        behavior: "auto",
      });
    }
  };

  const processMessage = (
    messageType: IframeMessageType,
    data: { height?: number | string; deltaY?: number }
  ) => {
    switch (messageType) {
      case IFRAME_MESSAGE_TYPES.RESIZE:
        if (
          typeof data.height === "number" ||
          typeof data.height === "string"
        ) {
          handleResizeMessage(data.height);
        }
        break;

      case IFRAME_MESSAGE_TYPES.SCROLL:
        if (typeof data.deltaY === "number") {
          handleScrollMessage(data.deltaY);
        }
        break;

      default:
        // Ignore unrecognized message types
        break;
    }
  };

  const handleMessage = (event: MessageEvent) => {
    if (customMessageProcessor) {
      const processedMessage = customMessageProcessor(event);
      if (processedMessage) {
        processMessage(processedMessage.type, processedMessage);
        return;
      }
    }

    if (!event.data || typeof event.data !== "object") return;

    processMessage(event.data.type, event.data);
  };

  window.addEventListener("message", handleMessage);

  // Return cleanup function
  return () => {
    window.removeEventListener("message", handleMessage);
  };
}

/**
 * Sends the height of an element to the parent window
 * @param element The element to measure
 * @param partSelectors Optional selectors for measuring by parts
 * @param heightBuffer Additional pixels to add to the height
 */
export function sendHeightToParent({
  element,
  partSelectors,
  heightBuffer = 0,
}: {
  element?: HTMLElement | null;
  partSelectors?: Record<string, string>;
  heightBuffer?: number;
} = {}): void {
  // If the current window is the parent, don't send the message
  if (window.parent === window) return;

  let height;

  // When partSelectors is provided, we measure individual sections separately and sum their heights.
  // This provides more accurate measurements than measuring the container alone, especially when:
  // 1. Some sections are scrollable (body) while others are fixed (header/footer)
  // 2. Content dynamically changes or loads asynchronously
  // 3. Complex layouts use fixed positioning or overflow properties
  if (partSelectors && Object.keys(partSelectors).length > 0) {
    let totalHeight = 0;
    const partHeights: Record<string, number> = {};

    Object.entries(partSelectors).forEach(([key, selector]) => {
      const el = document.querySelector(selector) as HTMLElement;
      if (el) {
        const partHeight = key.toLowerCase().includes("body")
          ? el.scrollHeight
          : el.getBoundingClientRect().height;

        totalHeight += partHeight;
        partHeights[key] = partHeight;
      }
    });

    if (totalHeight > 0) {
      height = totalHeight + heightBuffer;
    }
  }

  // If partSelectors is not provided, measure the provided element
  if (!height && element) {
    height = element.getBoundingClientRect().height + heightBuffer;
  } else if (!height) {
    // Fallback to document body
    height = document.body.scrollHeight;
  }

  if (height > 0) {
    window.parent.postMessage(
      {
        type: IFRAME_MESSAGE_TYPES.RESIZE,
        height,
      },
      "*"
    );
  }
}

/**
 * Sets up scroll event forwarding from iframe to parent window
 * This is used to allow the parent window to scroll when the user is scrolling in the iframe
 * @returns A cleanup function to remove event listeners
 */
export function setupScrollEventForwarding(): () => void {
  // If not in an iframe, don't do anything
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  if (window.parent === window) return () => {};

  // Cache for scrollable elements to avoid repeated DOM traversal and getComputedStyle calls
  const scrollableElementCache = new WeakMap<Element, Element>();

  // Optimized scrollable element finder with better caching
  const findScrollableElement = (targetElement: Element): Element => {
    if (!targetElement) return document.documentElement;

    // Check cache first
    const cached = scrollableElementCache.get(targetElement);
    if (cached) {
      return cached;
    }

    // For performance, limit traversal and prefer common scrollable elements
    let scrollableElement = targetElement;
    let depth = 0;
    const maxDepth = 10; // Limit DOM traversal depth

    while (
      scrollableElement &&
      scrollableElement !== document.body &&
      depth < maxDepth
    ) {
      // Fast check for common scrollable patterns before expensive getComputedStyle
      if (
        scrollableElement === document.documentElement ||
        scrollableElement.classList.contains("scroll") ||
        scrollableElement.scrollHeight > scrollableElement.clientHeight
      ) {
        const computedStyle = window.getComputedStyle(scrollableElement);
        const { overflowY } = computedStyle;

        if (overflowY === "auto" || overflowY === "scroll") {
          break;
        }
      }

      scrollableElement = scrollableElement.parentElement as Element;
      depth += 1;
    }

    // Default to document element if no scrollable container found
    if (!scrollableElement || depth >= maxDepth) {
      scrollableElement = document.documentElement;
    }

    // Cache the result
    scrollableElementCache.set(targetElement, scrollableElement);
    return scrollableElement;
  };

  // Optimized boundary check that uses cached scrollable elements
  const checkScrollBoundary = (
    targetElement: Element,
    deltaY: number
  ): boolean => {
    const scrollableElement = findScrollableElement(targetElement);
    const { scrollTop, scrollHeight, clientHeight } = scrollableElement;

    // Check if scrolling up and already at top
    if (deltaY < 0 && scrollTop <= 0) {
      return true; // Forward scroll to parent
    }

    // Check if scrolling down and already at bottom
    if (deltaY > 0 && scrollTop + clientHeight >= scrollHeight - 1) {
      return true; // Forward scroll to parent
    }

    // Element can still scroll internally
    return false;
  };

  // Handle wheel events to allow parent scrolling
  const handleWheel = (event: WheelEvent) => {
    const target = event.target as Element;

    // Check if the target or any parent element should be excluded from scroll forwarding
    const shouldExclude = SCROLL_EXCLUDED_ELEMENTS.some(
      (selector) => target.closest(selector) !== null
    );

    if (shouldExclude) {
      // Don't forward scroll events for excluded elements
      return;
    }

    // Check if we should forward the scroll to parent
    const shouldForwardScroll = checkScrollBoundary(target, event.deltaY);

    // Only forward scroll and prevent default if we're at scroll boundary
    if (shouldForwardScroll) {
      window.parent.postMessage(
        {
          type: IFRAME_MESSAGE_TYPES.SCROLL,
          deltaY: event.deltaY,
        },
        "*"
      );
      event.preventDefault();
    }
  };

  // Simplified touch event handling for performance
  let touchStartY = 0;
  let touchStartX = 0;
  let isVerticalScroll = false;
  let hasScrolled = false;

  const handleTouchStart = (event: TouchEvent) => {
    if (event.touches.length === 1) {
      touchStartY = event.touches[0].clientY;
      touchStartX = event.touches[0].clientX;
      isVerticalScroll = false;
      hasScrolled = false;
    }
  };

  const handleTouchMove = (event: TouchEvent) => {
    if (event.touches.length !== 1 || hasScrolled) return;

    const currentY = event.touches[0].clientY;
    const currentX = event.touches[0].clientX;

    const deltaY = touchStartY - currentY;
    const deltaX = Math.abs(touchStartX - currentX);

    // Determine scroll direction on first significant movement
    if (!isVerticalScroll && (Math.abs(deltaY) > 10 || deltaX > 10)) {
      isVerticalScroll = Math.abs(deltaY) > deltaX;

      // If it's horizontal scrolling, let native behavior handle it
      if (!isVerticalScroll) {
        return;
      }
    }

    // Only handle vertical scrolling at scroll boundaries
    if (isVerticalScroll && Math.abs(deltaY) > 10) {
      const target = event.target as Element;

      // Simple boundary check without expensive DOM queries
      const shouldForwardScroll = checkScrollBoundary(target, deltaY);

      if (shouldForwardScroll) {
        // Forward scroll to parent
        window.parent.postMessage(
          {
            type: IFRAME_MESSAGE_TYPES.SCROLL,
            deltaY: deltaY * 0.5, // Reduce sensitivity
          },
          "*"
        );

        hasScrolled = true; // Prevent multiple forwards for same gesture
        event.preventDefault();
      }
    }
  };

  const handleTouchEnd = () => {
    // Reset touch state - let native momentum handle the rest
    isVerticalScroll = false;
    hasScrolled = false;
  };

  // Add event listeners - use passive listeners where possible for better performance
  document.addEventListener("wheel", handleWheel, { passive: false });
  document.addEventListener("touchstart", handleTouchStart, { passive: true });
  document.addEventListener("touchmove", handleTouchMove, { passive: false });
  document.addEventListener("touchend", handleTouchEnd, { passive: true });
  document.addEventListener("touchcancel", handleTouchEnd, { passive: true });

  // Return cleanup function
  return () => {
    document.removeEventListener("wheel", handleWheel);
    document.removeEventListener("touchstart", handleTouchStart);
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", handleTouchEnd);
    document.removeEventListener("touchcancel", handleTouchEnd);
  };
}
